import { Document } from '@/entities/document';
import { DEFAULT_LOCALE } from '../hooks/use-locale';
import { ImageShape, VideoShape } from '../lib/types';
import { axiosAPI } from './axios-instance';
import { ApiResponse, AppLocale } from './lib/types';

export type SiteSettingsKeys = Partial<{
    main_first_screen_title: string;
    main_first_screen_big_text: string;
    main_first_screen_place: string;
    main_first_screen_date: string;
    main_first_screen_description: string;

    main_video_short: VideoShape;
    main_video_long: VideoShape;

    main_history_title: string;
    main_history_description: string;
    main_history_link_text: string;
    main_history_link: string;

    main_people_title: string;
    main_people_link_text: string;
    main_people_link: string;
    main_event_title: string;
    main_program_title: string;
    main_program_description: string;
    main_program_link_text: string;
    main_program_link: string;
    main_review_title: string;
    main_review_description: string;
    main_tariff_title: string;
    main_tariff_description: string;
    main_tariff_link_text: string;
    main_tariff_link: string;
    main_partner_title: string;
    main_partner_description: string;
    main_partner_button_text: string;
    main_other_event_title: string;
    main_other_event_description: string;
    main_other_event_link_text: string;
    main_other_event_link: string;
    main_post_title: string;
    main_post_link_text: string;
    main_post_link: string;

    participants_first_screen_title: string;
    participants_first_screen_caption: string;
    participants_first_screen_date: string;
    participants_first_screen_place: string;
    participants_first_screen_program_button_text: string;
    participants_first_screen_participate_button_text: string;
    participants_first_screen_partners_title: string;
    participants_first_screen_partner_images: ImageShape[];
    participants_first_screen_bg_video: VideoShape;
    participants_first_screen_bg_image: ImageShape;

    participants_key_formats_title: string;
    participants_key_formats_description: string;
    participants_key_formats_button_text: string;
    participants_key_formats_tickets_caption: string;

    participants_people_title: string;
    participants_people_description: string;

    participants_partners_title: string;
    participants_partners_description: string;

    participants_participants_list_form_title: string;
    participants_participants_list_form_description: string;
    participants_participants_list_form_partner_images: ImageShape[];

    participants_tariff_title: string;
    participants_tariff_description: string;
    participants_tariff_link_text: string;
    participants_tariff_link: string;
    participants_tariff_discount: string;
    participants_tariff_button_text: string;
    participants_tariff_document: Document;
    participants_tariff_disclaimer: string;
    participants_tariff_discount_price_label: string;
    participants_tariff_future_price_label: string;
    participants_tariff_footnote_description: string;
    participants_tariff_popup_title: string;
    participants_tariff_contact_title: string;
    participants_tariff_contact_description: string;
    participants_tariff_contact_phone: string;
    participants_tariff_contact_email: string;

    participants_history_title: string;
    participants_history_description: string;
    participants_history_link_text: string;
    participants_history_link: string;

    participants_event_title: string;

    participants_faq_title: string;

    participants_service_title: string;
    participants_service_description: string;
    participants_service_link_text: string;
    participants_service_link: string;

    participants_form_title: string;
    participants_form_description: string;
    participants_form_link_text: string;
    participants_form_link: string;

    participation_first_screen_title: string;
    participation_first_screen_description: string;
    participation_first_screen_document: Document;
    participation_choice_cards: {
        title: string;
        vertical_text: string;
        link: string;
        description?: string;
        image?: ImageShape;
    }[];
    participation_partners_title: string;
    participation_partners_button_text: string;
    participation_partners_description: string;
    participation_about_title: string;
    participation_about_description: string;
    participation_about_numbers: {
        value: string;
        label: string;
    }[];
    participation_form_title: string;
    participation_form_description: string;
    participation_form_link_text: string;
    participation_form_link: string;
    participation_participants_list_form_title: string;
    participation_participants_list_form_description: string;
    participation_participants_list_form_partner_images: ImageShape[];
    participation_faq_title: string;

    partners_company_title: string;
    partners_faq_title: string;

    visitors_expo_title: string;

    visitors_first_screen_title: string;
    visitors_first_screen_caption: string;
    visitors_first_screen_date: string;
    visitors_first_screen_place: string;
    visitors_first_screen_program_button_text: string;
    visitors_first_screen_participate_button_text: string;
    visitors_first_screen_partners_title: string;
    visitors_first_screen_partner_images: ImageShape[];
    visitors_first_screen_bg_video: VideoShape;
    visitors_first_screen_bg_image: ImageShape;

    visitors_about_quote: string;
    visitors_about_name: string;
    visitors_about_position: string;
    visitors_about_image: ImageShape;
    visitors_about_cards: {
        title: string;
        description?: string;
        image?: ImageShape;
    }[];

    visitors_key_formats_title: string;
    visitors_key_formats_description: string;
    visitors_key_formats_button_text: string;
    visitors_key_formats_tickets_caption: string;

    visitors_people_title: string;
    visitors_people_description: string;

    visitors_history_title: string;
    visitors_history_description: string;
    visitors_history_link_text: string;
    visitors_history_link: string;
    visitors_history_code: string;

    visitors_second_history_title: string;
    visitors_second_history_description: string;
    visitors_second_history_link_text: string;
    visitors_second_history_link: string;
    visitors_second_history_code: string;

    visitors_tariff_title: string;
    visitors_tariff_description: string;
    visitors_tariff_link_text: string;
    visitors_tariff_link: string;
    visitors_tariff_discount: string;
    visitors_tariff_button_text: string;
    visitors_tariff_document: Document;
    visitors_tariff_disclaimer: string;
    visitors_tariff_discount_price_label: string;
    visitors_tariff_future_price_label: string;
    visitors_tariff_footnote_description: string;
    visitors_tariff_popup_title: string;
    visitors_tariff_contact_title: string;
    visitors_tariff_contact_description: string;
    visitors_tariff_contact_phone: string;
    visitors_tariff_contact_email: string;

    visitors_event_title: string;
    visitors_event_description: string;
    visitors_event_button_text: string;

    visitors_register_title: string;
    visitors_register_subtitle: string;
    visitors_register_description: string;
    visitors_register_link_text: string;
    visitors_register_link: string;
    visitors_register_image: ImageShape;

    visitors_participants_list_form_title: string;
    visitors_participants_list_form_description: string;
    visitors_participants_list_form_partner_images: ImageShape[];

    visitors_themes_title: string;
    visitors_themes_cards: {
        title: string;
        image?: ImageShape;
    }[];

    visitors_scheme_title: string;
    visitors_scheme_description: string;
    visitors_scheme_button_text: string;

    visitors_program_title: string;
    visitors_program_description: string;

    visitors_form_title: string;
    visitors_form_description: string;

    visitors_faq_title: string;

    hr_first_screen_title: string;
    hr_first_screen_description: string;

    hr_history_title: string;
    hr_history_description: string;
    hr_history_link_text: string;
    hr_history_link: string;

    hr_people_title: string;

    hr_participate_title: string;
    hr_participate_description: string;
    hr_participate_link_text: string;
    hr_participate_link: string;

    hr_program_title: string;
    hr_program_document: Document;

    hr_tariff_title: string;
    hr_tariff_description: string;
    hr_tariff_link_text: string;
    hr_tariff_link: string;
    hr_tariff_discount: string;
    hr_tariff_contact_title: string;
    hr_tariff_contact_description: string;
    hr_tariff_contact_phone: string;
    hr_tariff_contact_email: string;
    hr_tariff_disclaimer: string;
    hr_tariff_discount_price_label: string;
    hr_tariff_future_price_label: string;

    hr_company_title: string;

    hr_about_title: string;
    hr_about_subtitle: string;
    hr_about_description: string;
    hr_about_image: ImageShape;

    hr_nominations_title: string;

    hr_selection_stages_title: string;
    hr_selection_stages_documents: Document[];

    hr_form_title: string;
    hr_form_description: string;

    hr_tariff_offer_document: Document;

    technological_first_screen_title: string;
    technological_first_screen_description: string;
    technological_first_screen_document: Document;

    technological_history_title: string;
    technological_history_description: string;
    technological_history_link_text: string;
    technological_history_link: string;

    technological_participate_title: string;
    technological_participate_description: string;
    technological_participate_link_text: string;
    technological_participate_link: string;

    technological_about_title: string;
    technological_about_subtitle: string;
    technological_about_description: string;
    technological_about_image: ImageShape;
    technological_about_link_text: string;
    technological_about_link: string;

    technological_company_title: string;
    technological_company_description: string;

    technological_directions_title: string;
    technological_directions_document: Document;

    technological_selection_stages_title: string;
    technological_selection_stages_documents: Document[];

    technological_reviews_title: string;
    technological_reviews_description: string;

    technological_form_title: string;
    technological_form_description: string;
    technological_form_link_text: string;
    technological_form_link: string;

    expo_first_screen_title: string;
    expo_first_screen_description: string;
    expo_first_screen_video: {
        src: string;
        type: string;
    }[];
    expo_first_screen_images: ImageShape[];
    expo_constructor_title: string;
    expo_constructor_button_text: string;
    expo_constructor_documents: Document[];
    expo_company_title: string;
    expo_history_title: string;
    expo_history_description: string;
    expo_history_link_text: string;
    expo_history_link: string;
    expo_review_title: string;
    expo_review_description: string;
    expo_form_title: string;
    expo_form_description: string;
    expo_faq_title: string;

    program_first_screen_title: string;
    program_first_screen_subtitle: string;

    program_other_event_title: string;
    program_other_event_description: string;
    program_other_event_button_text: string;

    program_faq_title: string;

    program_form_title: string;
    program_form_description: string;

    //new-program ----
    new_program_first_screen_title: string;
    new_program_first_screen_subtitle: string;
    new_program_first_screen_description: string;
    new_program_first_screen_description_caption: string;

    new_program_directions_title: string;
    new_program_directions_description: string;

    new_program_proposal_title: string;
    new_program_proposal_button_text: string;
    new_program_proposal_description: string;
    new_program_proposal_images: ImageShape[];

    new_program_faq_title: string;

    new_program_form_title: string;
    new_program_form_description: string;

    new_program_first_screen_documents: Document[];

    // -----

    calendar_first_screen_title: string;

    post_first_screen_title: string;
    post_first_screen_desription: string;
    post_first_screen_button_text: string;

    hotel_first_screen_title: string;
    hotel_first_screen_desription: string;
    hotel_first_screen_link: string;
    hotel_first_screen_link_text: string;

    hotel_hotels_title: string;
    hotel_hotels_desription: string;
    hotel_hotels_link: string;
    hotel_hotels_link_text: string;

    hotel_contact_title: string;
    hotel_contact_desription: string;

    car_first_screen_title: string;
    car_first_screen_desription: string;
    car_first_screen_link: string;
    car_first_screen_link_text: string;

    car_cars_title: string;
    car_cars_desription: string;
    car_cars_link: string;
    car_cars_link_text: string;

    car_conditions_title: string;
    car_conditions_desription: string;
    car_contact_title: string;
    car_contact_desription: string;

    accreditation_first_screen_title: string;
    accreditation_first_screen_desription: string;
    accreditation_first_screen_link: string;
    accreditation_first_screen_link_text: string;
    accreditation_accreditation_title: string;
    accreditation_accreditation_desription: string;
    accreditation_accreditation_link: string;
    accreditation_accreditation_link_text: string;
    accreditation_scheme_title: string;
    accreditation_scheme_document: Document;
    accreditation_contact_title: string;
    accreditation_contact_desription: string;

    shuttles_first_screen_title: string;
    shuttles_first_screen_desription: string;
    shuttles_first_screen_link: string;
    shuttles_first_screen_link_text: string;

    shuttles_about_title: string;
    shuttles_about_desription: string;
    shuttles_about_document: Document;
    shuttles_about_link: string;
    shuttles_about_link_text: string;

    shuttles_contact_title: string;
    shuttles_contact_desription: string;

    zone_first_screen_title: string;
    zone_first_screen_desription: string;
    zone_first_screen_link: string;
    zone_first_screen_link_text: string;

    zone_zones_title: string;
    zone_zones_link: string;
    zone_zones_link_text: string;

    zone_contact_title: string;
    zone_contact_desription: string;

    exchange_contacts_first_screen_title: string;
    exchange_contacts_first_screen_desription: string;
    exchange_contacts_tariff_title: string;
    exchange_contacts_tariff_desription: string;
    exchange_contacts_tariff_link_text: string;
    exchange_contacts_tariff_link: string;
    exchange_contacts_contact_title: string;
    exchange_contacts_contact_desription: string;

    register_first_screen_title: string;
    register_first_screen_desription: string;

    register_form_title: string;
    register_form_description: string;
    register_form_link_text: string;
    register_form_link: string;

    register_faq_title: string;

    privacy_content_text: string;

    contacts_first_screen_title: string;
    contacts_first_screen_desription: string;
    contacts_first_screen_link: string;
    contacts_first_screen_link_text: string;

    contacts_default_document_oferta: Document;
    contacts_default_phone: string;
    contacts_default_email: string;
    contacts_default_support_link: string;
    contacts_default_postal_сode: string;
    contacts_default_city: string;
    contacts_default_street: string;
    contacts_default_location: string;
    contacts_default_name: string;

    contacts_command_title: string;

    footer_default_form_text: string;

    premiya_tec_main_title: string;
    premiya_tec_main_dates: {
        caption: string;
        date: string;
    }[];
    premiya_tec_main_description: string;

    premiya_tec_first_screen_title: string;
    premiya_tec_first_screen_description: string;

    premiya_tec_company_title: string;

    premiya_tec_about_title: string;
    premiya_tec_about_subtitle: string;
    premiya_tec_about_description: string;
    premiya_tec_about_image: ImageShape;

    premiya_tec_themed_tracks_title: string;
    premiya_tec_themed_tracks_description: string;
    premiya_tec_themed_tracks_application_link: string;
    premiya_tec_themed_tracks_numbers: {
        value: string;
        label: string;
    }[];
    premiya_tec_themed_tracks_themed_tracks: {
        title: string;
        image: ImageShape;
    }[];

    premiya_tec_participate_title: string;
    premiya_tec_participate_button_text: string;
    premiya_tec_participate_button_link: string;

    premiya_tec_nominations_title: string;

    premiya_tec_selection_stages_title: string;
    premiya_tec_selection_stages_documents: Document[];

    premiya_tec_form_title: string;
    premiya_tec_form_description: string;

    premiya_tec_participate_description: string;
    premiya_tec_participate_link_text: string;
    premiya_tec_participate_link: string;

    premiya_tec_form_link_text: string;
    premiya_tec_form_link: string;

    premiya_tec_about_link_text: string;
    premiya_tec_about_link: string;

    team_first_screen_caption: string;
    team_first_screen_title: string;
    team_first_screen_description: string;
    team_first_screen_form_link_text: string;
    team_first_screen_form_link: string;
    team_about_title: string;
    team_about_description: string;
    team_about_images: ImageShape[];
    team_announcement_title: string;
    team_announcement_description: string;
    team_announcement_images: ImageShape[];
    team_work_title: string;
    team_work_advantages: {
        title: string;
        description: string;
        color: string;
    }[];
    team_selection_stages_title: string;
    team_selection_stages_stages: {
        name: string;
        caption?: string;
        content: {
            title: string;
            description: string;
        }[];
    }[];

    live_first_screen_description: string;
    live_first_screen_caption: string;
    live_first_screen_link_text: string;
    live_first_screen_link: string;
    live_first_screen_video: {
        src: string;
        alt: string;
        title: string;
    }[];
    live_icon_block_description: string;
    live_slider_slides: {
        title: string;
        description: string;
        image?: ImageShape;
    }[];
    live_schedule_title: string;
    live_schedule_description: string;
    live_schedule_items: {
        time: string;
        title: string;
        description: string;
        image?: ImageShape;
    }[];
    live_last_screen_title: string;
    live_last_screen_description: string;
    live_last_screen_button_text: string;
    live_last_screen_button_link: string;
    live_last_screen_caption: string;
}>;

export type ApiSiteSettingsRequest = {
    keys: (keyof SiteSettingsKeys)[];
};

type Params = {
    params: ApiSiteSettingsRequest;
    locale?: AppLocale;
    signal?: AbortSignal;
};

export type ApiSiteSettingsResponse = ApiResponse<SiteSettingsKeys>;

export const getSiteSettings = ({
    params,
    signal,
    locale = DEFAULT_LOCALE,
}: Params): Promise<ApiSiteSettingsResponse> =>
    axiosAPI
        .get<ApiSiteSettingsResponse>('/v1/settings', { params, signal, headers: { 'App-Locale': locale } })
        .then((res) => res.data);
